import { Button } from 'antd';
import React, { useCallback, useMemo } from 'react';
import styles from './Fwe.module.scss';
import Link from 'antd/lib/typography/Link';
import { IFolder, ITenant } from '../../../../../types/api';
import {
  EVENT_LOCALMODEL_POINT_CREATED,
  trackUpdate,
} from '../../../../../utils/matomoEvents';
import Api from '../../../../../api/Api';

interface IFWEPointOrigin {
  tenant: ITenant;
  folder: IFolder;
}

interface IFwePointPopup {
  coordinates: number[];
  name: string;
  datasetId: string;
  fwePointOrigin: IFWEPointOrigin;
  fweUrl: string;
  loadingDelete: boolean;
  createNewPoint: (coordinates: Array<number>, name?: string) => void;
  handleClose: () => void;
  setNotAuthorizedUserPopup: (item: boolean) => void;
  setConfirmationDeletePopup: (item: boolean) => void;
  setLoadingDelete: (item: boolean) => void;
}

const FwePointPopup: React.FC<IFwePointPopup> = ({
  coordinates,
  name,
  fwePointOrigin,
  loadingDelete,
  setLoadingDelete,
  createNewPoint,
  fweUrl,
  handleClose,
  datasetId,
  setNotAuthorizedUserPopup,
  setConfirmationDeletePopup,
}) => {
  const fweLink = useMemo(() => {
    // ToDo: Once backend is ready:
    // To activate "See in FWE" link user needs to be member of fwePointOrigin.tenant and have read access to fwePointOrigin.folder
    // --> MOOD backend will filter out points outside of user's tenant
    // --> MOOD backend will provide an endpoint returning project capabilities
    // this endpoint needs to be called and deactivate the fweLink if user has no access rights to the folder
    return (
      fweUrl +
      '/project/' +
      fwePointOrigin.folder.id +
      '?tenantId=' +
      fwePointOrigin.tenant.id
    );
  }, [fweUrl, fwePointOrigin]);

  const handleCreateNewPoint = useCallback(() => {
    trackUpdate(
      EVENT_LOCALMODEL_POINT_CREATED,
      coordinates,
      fwePointOrigin.folder.name,
      ''
    );
    createNewPoint(coordinates, name);
    handleClose();
  }, [
    coordinates,
    name,
    createNewPoint,
    fwePointOrigin.folder.name,
    handleClose,
  ]);

  const handleConfirmDelete = useCallback(async () => {
    try {
      setLoadingDelete(true);
      const canDelete = await Api.service.canDeleteFWEdata(datasetId);
      if (canDelete) {
        setConfirmationDeletePopup(true);
      } else {
        setNotAuthorizedUserPopup(true);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingDelete(false);
    }
  }, [datasetId]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.title}>
          <b>{name}</b>
        </div>
        <div className={styles.coordinates}>
          <div>Long {Math.round(coordinates[0] * 10000) / 10000}</div>
          <div className={styles.leftPadding}>
            Lat {Math.round(coordinates[1] * 10000) / 10000}
          </div>
        </div>
        <div className={styles.folder}>
          Folder Name: {fwePointOrigin.folder.name}
        </div>
        <Link href={fweLink} target='_blank' rel='noopener' className='fweLink'>
          {'See in MIKE MS'}
        </Link>
        <div className={styles.buttonWrapper}>
          <Button
            type='default'
            onClick={handleConfirmDelete}
            className={styles.buttonDelete}
            loading={loadingDelete}
          >
            {loadingDelete ? 'Removing...' : 'Remove dataset from MOOD'}
          </Button>
          <Button
            type='primary'
            onClick={handleCreateNewPoint}
            className={styles.button}
          >
            New point in this location
          </Button>
        </div>
      </div>
    </>
  );
};

export default FwePointPopup;
